/**
 * ═══════════════════════════════════════════════════════════════════════
 * LoginPage — Password-based Login
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Flow:
 *   1. Pick userType (INDIVIDUAL / ORGANISATION)
 *   2. Pick loginMethod (PHONE / EMAIL)
 *   3. Enter identifier + password
 *   4. Call authApi.login() → get JWT tokens + session data
 *   5. Store session, route to agent page
 *
 * OTP is NOT used here. Forgot-password is a separate page.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { login, signOut } from '../services/auth/authApi';
import { getAuthErrorMessage } from '../services/auth/authErrors';
import { normalizeIndianMobile, validationMessage } from '../utils/phone';
import { STORAGE_KEYS } from '../constants';
import { ROUTES } from '../constants/routes';
import { clearStoredSession, storeSession, getSession } from '../services/api/sessionApi';
import { setAuthToken, scheduleTokenRefresh } from '../services/api/apiClient';

import AuthCard from '../components/auth/AuthCard';
import UserTypeSelector from '../components/auth/UserTypeSelector';
import LoginMethodSelector from '../components/auth/LoginMethodSelector';
import PhoneInput from '../components/auth/PhoneInput';
import EmailInput from '../components/auth/EmailInput';
import PasswordInput from '../components/auth/PasswordInput';
import PrimaryButton from '../components/auth/PrimaryButton';

// ── Login method options per user type ────────────────────────────────

const INDIVIDUAL_METHODS = ['PHONE', 'EMAIL'];
const ORGANISATION_METHODS = ['USERNAME', 'EMAIL'];

// ── Helpers ──────────────────────────────────────────────────────────

function validateEmail(email) {
  if (!email) return 'Enter your email';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email';
  return '';
}

// ── Component ────────────────────────────────────────────────────────

export default function LoginPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const allowIndividual = searchParams.get('mode') === 'individual';

  // ── local form state ─────────────────────────────────────────────
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true'
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Idle-timeout notice — surfaced after the refresh Lambda returns 401
  // with code IDLE_TIMEOUT, stashed by apiClient._doRefresh.
  const [idleNotice, setIdleNotice] = useState('');

  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('__ak_logout_reason');
      if (reason === 'IDLE_TIMEOUT') {
        setIdleNotice('You were signed out due to inactivity. Please log in again to continue.');
        sessionStorage.removeItem('__ak_logout_reason');
      }
    } catch { /* SSR / privacy mode */ }
  }, []);

  const { userType, loginMethod, setUserType, setLoginMethod } = auth;

  // Available login methods depend on user type
  const availableMethods = userType === 'ORGANISATION' ? ORGANISATION_METHODS : INDIVIDUAL_METHODS;

  // ── validation ───────────────────────────────────────────────────
  const phoneError = useMemo(
    () => (loginMethod === 'PHONE' ? validationMessage(phone) : ''),
    [phone, loginMethod],
  );
  const emailError = useMemo(
    () => (loginMethod === 'EMAIL' ? validateEmail(email) : ''),
    [email, loginMethod],
  );
  const usernameError = useMemo(
    () => {
      if (loginMethod !== 'USERNAME') return '';
      if (!username) return 'Enter your username';
      if (username.length < 3) return 'Username must be at least 3 characters';
      if (!/^[a-zA-Z0-9_.]+$/.test(username)) return 'Only letters, numbers, underscores, and dots';
      return '';
    },
    [username, loginMethod],
  );

  const identifierValid =
    loginMethod === 'PHONE'
      ? !phoneError && phone.length > 0
      : loginMethod === 'USERNAME'
        ? !usernameError && username.length > 0
        : !emailError && email.length > 0;

  const formValid = identifierValid && password.length > 0;

  // ── submit login ─────────────────────────────────────────────────
  const handleLogin = async () => {
    setError('');
    if (!formValid) return;
    setLoading(true);
    try {
      // Build identifier
      let identifier;
      if (loginMethod === 'PHONE') {
        const { e164 } = normalizeIndianMobile(phone);
        identifier = e164;
      } else if (loginMethod === 'USERNAME') {
        identifier = username.trim();
      } else {
        identifier = email.trim();
      }

      // Clear stale session data
      clearStoredSession();

      // Call password-based login endpoint
      const result = await login({ identifier, password, rememberMe });

      // Handle challenge (e.g. NEW_PASSWORD_REQUIRED for manually-created users)
      if (result.challenge || result.requiresPasswordChange) {
        auth.setUsername(result.username || identifier);
        auth.setUserType(userType);
        navigate(ROUTES.SET_PASSWORD, {
          replace: true,
          // session = proof of this login; set-password requires it (bk-1525)
          state: { username: result.username || identifier, session: result.session },
        });
        return;
      }

      // Persist Remember Me preference BEFORE storing tokens
      // This controls whether tokens go to localStorage vs sessionStorage
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, rememberMe ? 'true' : 'false');

      // Store JWT in memory so apiClient attaches it to subsequent requests
      if (result?.idToken) {
        setAuthToken(result.idToken);
      }

      // Session metadata is populated below from /api/session — the
      // canonical source. We intentionally do NOT call storeSession(result)
      // here because the login response carries userId = <raw identifier the
      // user typed> while /api/session returns userId = <canonical user_id
      // from user_profiles>. Two writes with two different string formats
      // for the same person used to confuse the F1 identity guard.

      // Fetch authoritative session from backend (if available)
      let sessionData;
      try {
        sessionData = await getSession();
      } catch (sessionErr) {
        const msg = sessionErr?.message;
        if (msg === 'DEVICE_NOT_APPROVED' || msg === 'DEVICE_ID_MISSING') {
          // RC-2 (STAFF-DEVICE-LOCK): login() already minted a valid Cognito
          // session before /api/session refused this device. Sign out BEFORE
          // surfacing the message so a denied device never keeps a working
          // session against any other endpoint.
          clearStoredSession();
          await signOut();
          throw sessionErr;
        }
        if (msg === 'FORBIDDEN' || msg === 'UNAUTHORIZED' || msg === 'ACCOUNT_DISABLED') {
          // Backend explicitly denied — clear any partial session and surface the error
          clearStoredSession();
          throw sessionErr;
        }
        // Network error, timeout, or 5xx — clear partial session and show friendly error
        clearStoredSession();
        const err = new Error('BACKEND_UNAVAILABLE');
        err.code = 'BACKEND_UNAVAILABLE';
        throw err;
      }

      storeSession(sessionData);
      auth.loginSuccess({ accessToken: result.accessToken || null });

      // Route by assigned agent
      if (sessionData.agent === 'crowchat') {
        navigate(ROUTES.ORG, { replace: true });
      } else {
        navigate(ROUTES.CHAT, { replace: true });
      }

      // Start proactive token refresh timer AFTER navigation — non-blocking.
      // Timer setup has no UX impact and shouldn't delay the route change.
      try { scheduleTokenRefresh(); } catch (_) { /* non-critical */ }
    } catch (e) {
      if (e?.message === 'FORBIDDEN') {
        setError('Access Denied: You do not have permission to access this application.');
      } else if (e?.message === 'DEVICE_NOT_APPROVED' || e?.message === 'DEVICE_ID_MISSING') {
        // Terminal — no retry/refresh. Clear the password so a re-submit on
        // this (still-unapproved) device doesn't silently resend the same creds.
        setPassword('');
        setError(getAuthErrorMessage(e));
      } else {
        setError(getAuthErrorMessage(e));
      }
    } finally {
      setLoading(false);
    }
  };

  // ── reset when selectors change ──────────────────────────────────
  const handleUserTypeChange = (val) => {
    setUserType(val);
    // Reset loginMethod to first available for the new userType
    const methods = val === 'ORGANISATION' ? ORGANISATION_METHODS : INDIVIDUAL_METHODS;
    if (!methods.includes(loginMethod)) {
      setLoginMethod(methods[0]);
    }
    resetForm();
  };
  const handleLoginMethodChange = (val) => {
    setLoginMethod(val);
    resetForm();
  };
  const resetForm = () => {
    setPhone('');
    setEmail('');
    setUsername('');
    setPassword('');
    setError('');
  };

  // ── handle Enter key ─────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && formValid && !loading) {
      handleLogin();
    }
  };

  // ── render ───────────────────────────────────────────────────────
  return (
    <AuthCard title="Log In" subtitle="Sign in to your account" error={error}>
      {idleNotice && (
        <div
          role="status"
          className="bg-amber-50 dark:bg-amber-900/40 border border-amber-400 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-sm rounded-md px-3 py-2 mb-3"
        >
          {idleNotice}
        </div>
      )}
      <UserTypeSelector value={userType} onChange={handleUserTypeChange} disabled={loading} allowIndividual={allowIndividual} />
      <LoginMethodSelector
        value={loginMethod}
        onChange={handleLoginMethodChange}
        disabled={loading}
        methods={availableMethods}
      />

      {loginMethod === 'PHONE' ? (
        <PhoneInput value={phone} onChange={setPhone} error={phoneError} disabled={loading} />
      ) : loginMethod === 'USERNAME' ? (
        <div style={{ marginBottom: 0 }}>
          <label
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 500,
              marginBottom: 6,
              color: theme.auth.labelColor,
            }}
          >
            Username
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter your username"
            disabled={loading}
            autoComplete="username"
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: 15,
              borderRadius: 8,
              border: `1px solid ${usernameError && username ? theme.auth.errorBorder || '#FCA5A5' : theme.auth.inputBorder}`,
              backgroundColor: theme.auth.inputBg,
              color: theme.auth.inputText,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {usernameError && username && (
            <div style={{ color: theme.auth.errorText || '#DC2626', fontSize: 12, marginTop: 4 }}>
              {usernameError}
            </div>
          )}
        </div>
      ) : (
        <EmailInput value={email} onChange={setEmail} error={emailError} disabled={loading} />
      )}

      <div onKeyDown={handleKeyDown}>
        <PasswordInput
          value={password}
          onChange={setPassword}
          disabled={loading}
          label="Password"
          placeholder="Enter your password"
        />
      </div>

      <div style={{ height: 12 }} />

      {/* Remember Me checkbox */}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          color: theme.auth.labelColor || theme.auth.mutedText,
          cursor: 'pointer',
          userSelect: 'none',
          marginBottom: 12,
        }}
      >
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          disabled={loading}
          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: theme.auth.buttonPrimary }}
        />
        Keep me logged in
      </label>

      <PrimaryButton onClick={handleLogin} disabled={!formValid || loading}>
        {loading ? 'Signing in…' : 'Sign In'}
      </PrimaryButton>

      {/* footer links */}
      <div style={{ marginTop: 12, textAlign: 'right', fontSize: 13 }}>
        <Link to={ROUTES.FORGOT_PASSWORD} style={{ color: theme.auth.mutedText, textDecoration: 'none' }}>
          Forgot password?
        </Link>
      </div>
      <div style={{ marginTop: 12, textAlign: 'center', fontSize: 13, color: theme.auth.mutedText }}>
        Don't have an account?{' '}
        <Link to={`${ROUTES.SIGNUP}${allowIndividual ? '?mode=individual' : ''}`} style={{ color: theme.auth.buttonPrimary, textDecoration: 'none', fontWeight: 600 }}>
          Sign Up
        </Link>
      </div>
    </AuthCard>
  );
}
